import type { JSX } from 'react';
import { Table, Text } from '@mantine/core';
import { calculateVariant } from '@pst/shared';
import type { Offer } from '../../api';
import { CONSTELLATION_LABELS, formatAmount, formatEuro, formatHours, formatPercent } from '../../format';
import { Section } from '../../ui/Section';

/**
 * The variants side by side - the numbers of the decision paper: sums,
 * hours, revenue per hour, subcontracting, reserve. Pros, cons and risks
 * belong to the texts (and, with stage 3, to the evaluation).
 */
export function OfferCompare({ offer }: { offer: Offer }): JSX.Element {
  const rows = offer.variants.map((v) => ({ v, r: calculateVariant(v.data) }));
  const line = (label: string, f: (x: (typeof rows)[number]) => string, hint?: string): JSX.Element => (
    <Table.Tr>
      <Table.Td><Text size="sm">{label}</Text>{hint && <Text size="xs" c="dimmed">{hint}</Text>}</Table.Td>
      {rows.map((x) => <Table.Td key={x.v.id} ta="right"><Text size="sm">{f(x)}</Text></Table.Td>)}
    </Table.Tr>
  );
  return (
    <Section title="Vergleich der Varianten" description={offer.variants.length < 2 ? 'Erst eine zweite Variante anlegen (Reiter Kalkulation › + Variante).' : 'Leistungsumfang und Stunden können gleich sein; dann unterscheidet sich nur die Konstellation.'}>
      <Table.ScrollContainer minWidth={500}>
        <Table verticalSpacing={4} className="pst-tabular">
          <Table.Thead>
            <Table.Tr><Table.Th>Kennzahl</Table.Th>{rows.map((x) => <Table.Th key={x.v.id} ta="right">{x.v.code} — {x.v.name}<Text size="xs" c="dimmed" fw={400}>{CONSTELLATION_LABELS[x.v.constellation]}</Text></Table.Th>)}</Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {line('Angebotssumme netto', (x) => formatEuro(x.r.net))}
            {line('Angebotssumme brutto', (x) => formatEuro(x.r.gross))}
            {line('Eigene Stunden', (x) => formatHours(x.r.ownHours))}
            {line('Stunden der Nachunternehmer', (x) => (x.r.subcontractorHours ? formatHours(x.r.subcontractorHours) : '—'))}
            {line('Fremdleistung inkl. Zuschlag', (x) => formatEuro(x.r.foreign), 'Anteil an der Summe in Klammern')}
            {line('', (x) => (x.r.net ? `(${formatPercent(x.r.foreignShare * 100)})` : ''))}
            {line('Erlös je eigener Stunde', (x) => (x.r.revenuePerHour === null ? '—' : `${formatAmount(x.r.revenuePerHour)} €${x.r.belowThreshold ? ' – unter Warnschwelle' : ''}`))}
            {line('Reserve aus Aufrundung', (x) => formatEuro(x.r.rounding))}
            {line('Positionen · Zusatzleistungen', (x) => `${x.v.data.positions.length} · ${x.v.data.extras.length}`)}
            {line('Preise der Nachunternehmer', (x) => (x.v.data.subcontractors.length === 0 ? '—' : x.v.data.subcontractors.every((s) => s.priceStatus === 'bestätigt') ? 'bestätigt' : 'Annahme – nicht belastbar'))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Section>
  );
}
